import { useCallback, useEffect } from "react";
import { useUserStore } from "@/stores/useUserStore.ts";
import {
  type DispatchGPTTextLoading,
  SignalInvoke,
  SignalOn,
  SocratesSessionStatus,
} from "@/types/signalr-functions.ts";
import { toastAndError, toastAndWarn } from "@/helpers/toast.ts";
import { None } from "ts-results-es";
import { useAssignedChatStore } from "@/stores/useAssignedChatStore.ts";
import type { FlowItem } from "@/types/flow.ts";
import { PollingStates, useChatStore } from "@/stores/useChatStore.ts";
import { deriveHeldReplyState, getChatResponse, getChatSocratesSession } from "@/api/chat.ts";
import { chatResponseQueryKey } from "@/helpers/heldReply.ts";
import { useSettingsStore } from "@/stores/useSettingsStore.ts";
import audioNotification from "@/assets/audio/notification.mp3";
import { PlayAudio } from "@/helpers/audio.ts";
import { metrics } from "@sentry/react";
import { useInputStore } from "@/stores/useInputStore.ts";
import { interventionStorageCollection } from "@/collections/interventionStorageCollection.ts";
import { ADT } from "pb.adt";
import { JsonParse } from "@/helpers/json.ts";
import { useSignalStore } from "@/stores/useSignalrStore.ts";
import { useQueryClient } from "@tanstack/react-query";
import { getFlowGeneratorSession } from "@/api/admin.ts";
import { flowSessionQueryKey } from "@/helpers/flowSession.ts";

export const useHookSignalrChat = () => {
  const {
    chats,
    PrependNewMessage,
    IncrementUnreadCounter,
    SetLlmMessage,
    AppendHookCallback,
    SetSocratesState,
    SetSocratesSession,
    SetStartupFinished,
    SetFlaggedMessage,
    SetFlowGeneratorSession,
  } = useChatStore();
  const { SetSessionCompleted, UpdateSessionDetails } = useAssignedChatStore();
  const { userId } = useUserStore();
  const { signalr } = useSignalStore();

  const queryClient = useQueryClient();

  // Handles everything related to appending a new message & incrementing unread counters
  const onChatUpdate = useCallback(
    (flowSessionId: number) => {
      if (!signalr) return None;

      return signalr.On(SignalOn.DispatchFlowItem + flowSessionId, (flow: FlowItem) => {
        PrependNewMessage(flowSessionId, flow);

        const { assignedSessions } = useAssignedChatStore.getState();

        const session = assignedSessions.get(flow.flowGeneratorSessionId);
        if (!session || session.completed) return;

        if (flow.flowItemType === "Information" && flow.text === "Auto unclaimed") {
          // Sometimes we go AFK, and a session is auto-unclaimed
          // Set the session to unclaimed
          // Note: We also hook into signalr events to handle this
          SetSessionCompleted(flowSessionId);
        }

        const { chats } = useChatStore.getState();
        const chat = chats.get(flow.flowGeneratorSessionId);
        if (!chat) return;

        // If message is not from tutor or bot, play bleep sound
        if (flow.userId !== userId && flow.userId !== 0 && flow.userId !== -1) {
          const { activeChat } = useChatStore.getState();
          const { settings } = useSettingsStore.getState();
          const TextBlip = () => {
            PlayAudio(audioNotification).catch((e) => {
              metrics.count("audio_notification_failure", 1);
              console.error("Failed to play notifications", e);
            });
          };
          if (flow.flowGeneratorSessionId !== activeChat.unwrapOrElse(() => 0)) {
            IncrementUnreadCounter(flow.flowGeneratorSessionId);
            if (settings.AudioChatBlip) TextBlip();
          } else if (settings.AudioChatBlip && settings.AudioChatAlways) {
            TextBlip();
          }
        }
      });
    },
    [PrependNewMessage, IncrementUnreadCounter, signalr, userId, SetSessionCompleted],
  );

  const onLLMUpdate = useCallback(
    (flowSessionId: number) => {
      if (!signalr) return None;

      return signalr.On(SignalOn.DispatchGPTText + flowSessionId, (text: string) => {
        if (text != "") SetLlmMessage(flowSessionId, text);
      });
    },
    [SetLlmMessage, signalr],
  );

  const onLLMLoading = useCallback(
    (flowSessionId: number) => {
      if (!signalr) return None;

      return signalr.On(SignalOn.DispatchGPTTextLoading + flowSessionId, (data) => {
        const { assignedSessions } = useAssignedChatStore.getState();
        const { SetAiLoading } = useInputStore.getState();
        let state = JsonParse<DispatchGPTTextLoading>(data);

        if (!state) {
          console.warn(
            "TextLoading got given an invalid string that could not get parsed to boolean",
            data,
          );
          return;
        }

        SetAiLoading(flowSessionId, state?.isLoading);

        // Update sessions that don't have an intervention id
        if (assignedSessions.get(flowSessionId)?.assignedChat.flowGeneratorSessionInterventionId)
          return;

        UpdateSessionDetails(state.flowGeneratorSessionId, {
          flowGeneratorSessionInterventionId: state.flowGeneratorSessionInterventionId,
        });
      });
    },
    [signalr, UpdateSessionDetails],
  );

  const onLLMFlowFailure = useCallback(
    (flowSessionId: number) => {
      if (!signalr) return None;

      return signalr.On(SignalOn.DispatchGPTError + flowSessionId, (data) => {
        console.warn("GPT Dispatch error:", flowSessionId, data);
        metrics.count("GPT failure", 1);
      });
    },
    [signalr],
  );

  useEffect(() => {
    if (!signalr) return;

    // Assign hooks to our chats if needed
    [...chats.values()]
      .filter((chat) => !chat.isStartupFinished)
      .forEach((chat) => {
        AppendHookCallback(chat.flowGeneratorSessionId, [
          onChatUpdate(chat.flowGeneratorSessionId),
          onLLMUpdate(chat.flowGeneratorSessionId),
          onLLMLoading(chat.flowGeneratorSessionId),
          onLLMFlowFailure(chat.flowGeneratorSessionId),
        ]);

        signalr
          .Invoke(SignalInvoke.JoinAiTutorResponseGroup, chat.flowGeneratorSessionId.toString())
          .catch((e) => {
            toastAndWarn("Failed to join ai tutor group for " + chat.flowGeneratorSessionId, e);
          });

        const { assignedSessions } = useAssignedChatStore.getState();
        const assigned = assignedSessions.get(chat.flowGeneratorSessionId);

        interventionStorageCollection.startSyncImmediate();
        if (!interventionStorageCollection.get(chat.flowGeneratorSessionId)) {
          interventionStorageCollection.insert({
            id: chat.flowGeneratorSessionId,
            lastSocratesSessionId: chat.socrates.session?.learnerSessionId ?? "",
            isFeedbackComplete: assigned?.feedbackGiven ?? false,
            lastInteractedOn: new Date().toDateString(),
          });
        } else {
          interventionStorageCollection.update(chat.flowGeneratorSessionId, (draft) => {
            draft.lastInteractedOn = new Date().toDateString();
          });
        }

        if (!chat.flowGeneratorSession) {
          queryClient
            .query({
              queryFn: () =>
                getFlowGeneratorSession(
                  chat.flowGeneratorSessionId ?? -5 /* Just setting to -5 for debugging reasons*/,
                ),
              queryKey: flowSessionQueryKey(chat?.flowGeneratorSessionId ?? -5),
              staleTime: 1000 * 60,
            })
            .then((data) => {
              if (data) {
                SetFlowGeneratorSession(chat.flowGeneratorSessionId, data);
              }
            });
        }

        queryClient
          .query({
            queryFn: () => getChatResponse(chat.flowGeneratorSessionId),
            queryKey: chatResponseQueryKey(chat.flowGeneratorSessionId),
            retry: 2,
          })
          .then((data) => {
            if (!data) return;

            if (data.heldStudentMessage) {
              const parsedFlagged = deriveHeldReplyState(data);

              SetFlaggedMessage(chat.flowGeneratorSessionId, parsedFlagged);
            }

            if (data.message && chat.llmMessage === "") {
              SetLlmMessage(chat.flowGeneratorSessionId, data.message);
            }
          })
          .catch((e) => {
            toastAndError("Could not fetch existing llm message", e);
          });

        SetStartupFinished(chat.flowGeneratorSessionId);
      });

    // Poll endpoints for extra info if needed
    [...chats.values()]
      .filter((chat) => ADT.match(chat.socrates.state, "Idle"))
      .filter((chat) => chat.flowGeneratorSession)
      .forEach((chat) => {
        const { assignedSessions } = useAssignedChatStore.getState();
        const session = assignedSessions.get(chat.flowGeneratorSessionId);
        if (!session || session.completed) return;

        SetSocratesState(chat.flowGeneratorSessionId, PollingStates.Fetching({ attempts: 0 }));

        let debugRetryCounter = 0;
        let socRetryCount = 0;
        const pollSoc = () => {
          // There are two retries, one for failures, the other because we need to poll until ready
          const retry = (skipAddition: boolean) => {
            if (socRetryCount >= 10) {
              metrics.count("Socrates Early Exit", 1);
              SetSocratesState(
                chat.flowGeneratorSessionId,
                PollingStates.Failed({
                  reason: "Socrates failed to return any valid data, bailing",
                }),
              );
              return;
            }
            if (!skipAddition) socRetryCount += 1;

            debugRetryCounter += 1;

            SetSocratesState(
              chat.flowGeneratorSessionId,
              PollingStates.Fetching({ attempts: debugRetryCounter }),
            );

            setTimeout(() => {
              pollSoc();
            }, 1000);
          };

          queryClient
            .query({
              queryFn: () =>
                getChatSocratesSession(chat.flowGeneratorSessionId, [
                  { name: "includes", value: "active-pedagogical-plan" },
                  { name: "includes", value: "pedagogical-plans" },
                  { name: "includes", value: "interactions" },
                ]),
              queryKey: ["getChatSocratesSession", chat.flowGeneratorSessionId],
              staleTime: 500,
              retry: (failureCount) => {
                if (failureCount > 5) {
                  SetSocratesState(
                    chat.flowGeneratorSessionId,
                    PollingStates.Failed({
                      reason: "Max retry attempts reached, Socrates did not return any data",
                    }),
                  );
                  return false;
                }

                debugRetryCounter += 1;

                SetSocratesState(
                  chat.flowGeneratorSessionId,
                  PollingStates.Fetching({ attempts: debugRetryCounter }),
                );
                return true;
              },
            })
            .then((data) => {
              if (data) {
                SetSocratesSession(chat.flowGeneratorSessionId, data);

                if (
                  data.status.toLowerCase() === SocratesSessionStatus.Initializing.toLowerCase()
                ) {
                  retry(false);
                } else if (data.status.toLowerCase() === "active") {
                  SetSocratesState(chat.flowGeneratorSessionId, PollingStates.Ready());
                  try {
                    interventionStorageCollection.update(chat.flowGeneratorSessionId, (draft) => {
                      draft.lastSocratesSessionId = data.learnerSessionId;
                    });
                  } catch (e) {
                    console.error(e);
                  }

                  metrics.gauge("Socrates Success", 1);
                  metrics.gauge("Socrates Retry", debugRetryCounter);
                }
              } else {
                retry(false);
              }
            })
            .catch((e) => {
              metrics.gauge("Socrates Hard Error", 1);
              toastAndError("Could not fetch socratesSession data", e);
            });
        };

        pollSoc();
      });
  }, [
    AppendHookCallback,
    SetStartupFinished,
    onChatUpdate,
    SetFlaggedMessage,
    onLLMFlowFailure,
    onLLMUpdate,
    signalr,
    onLLMLoading,
    SetSocratesState,
    SetSocratesSession,
    queryClient,
    SetLlmMessage,
    SetFlowGeneratorSession,
    chats,
  ]);
};
